/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { GenericCard } from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";

import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { mapCloudDetailToSections } from "kontrolEdge/constants/constants";
import {
  CCDetailSection,
  HistoryRow,
  TableColumn,
} from "./CloudConnectivityDetailsData";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import type { ServiceStatusVariant } from "kontrolEdge/helpers/serviceStatusTag";
import { useMetaEditor } from "kontrolEdge/components/Shared/NameTagEdit/useMetaEditor";
import { EditableMetaRow } from "kontrolEdge/components/Internet/InternetDetails/utilities/EditableMetaRows";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { formatDateTimeTZ } from "kontrolEdge/helpers/formattersShappedData";
import { CloudConnectivityDetail } from "./CloudConnectivityDetails";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { usePhoneFold } from "kontrolEdge/components/Shared/DetailSummary/usePhoneFold";

interface Props {
  data: CCDetailSection;
  // connectionDetail: {
  //   parentConnection: string;
  // };
  connectionDetail: CloudConnectivityDetail;
  tenantId: number;
  /** Pre-hydrated tag list from the detail GET inline `tags` field (§5.5). */
  initialAssignedTags?: ResourceTagSummary[];
  /** Tenant the resource actually lives in (`connectionDetail.tenantId`). See PortDetails.tsx for rationale. */
  resourceTenantId?: number;
}

// ---- filters (local to History modal) ----
type MonthValue =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12"
  | "";

const MONTHS: { value: MonthValue; label: string }[] = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

const CloudDetailsCard: React.FC<Props> = ({
  data,
  connectionDetail,
  tenantId,
  initialAssignedTags,
  resourceTenantId,
}) => {
  const api = useAxiosAuth0();
  const navigate = useNavigate();

  // Custom scrollbar styles for Cloud Connection ID
  // React.useEffect(() => {
  //   const style = document.createElement('style');
  //   style.textContent = `
  //     .custom-scrollbar::-webkit-scrollbar {
  //       height: 5px;
  //       padding-top: 1px;
  //     }
  //     .custom-scrollbar::-webkit-scrollbar-track {
  //       background: #f3f4f6;
  //       border-radius: 10px;
  //     }
  //     .custom-scrollbar::-webkit-scrollbar-thumb {
  //       background: #d1d5db;
  //       border-radius: 10px;
  //     }
  //     .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  //       background: #9ca3af;
  //     }
  //   `;
  //   document.head.appendChild(style);
  //   return () => {
  //     document.head.removeChild(style);
  //   };
  // }, []);

  //Port id selected from MainPort
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get("connectionId");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rawConnectionData, setRawConnectionData] = useState<any>(null);
  const [cloudDetailsData, setCloudDetailsData] = useState<CCDetailSection[]>(
    []
  );
  const shouldCollapse = data.isCollapsible ?? true;
  // Folded on a phone, with a one-line summary (CTX-1421).
  const fold = usePhoneFold(data, { isCollapsible: shouldCollapse, initialExpanded: data.initialExpanded });
  const tableFold = usePhoneFold(data, { isCollapsible: true });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  // Close the alert
  const closeAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  // Confirm button on the alert just closes it
  const confirmAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  // Logic for Name and Tag editing
  const nameSeed = String(
    data.items.find((i) => i.label === "Connection Name")?.value ?? ""
  );
  const tagSeed = (() => {
    const v = data.items.find((i) => i.label === "Tag")?.value;
    return Array.isArray(v) ? v.join(", ") : String(v ?? "");
  })();

  const meta = useMetaEditor({
    api,
    kind: "cloud",
    resourceId: connectionId,
    tenantId,
    initialName: nameSeed,
    initialTag: tagSeed,
  });

  // Convert tag string to array for EditableTagList
  const tagArray = useMemo(() => {
    if (!meta.tagValue || meta.tagValue.trim() === "") return [];
    return meta.tagValue.split(",").map(t => t.trim()).filter(t => t !== "");
  }, [meta.tagValue]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [filterYear, setFilterYear] = useState<string>("");
  const [filterMonth, setFilterMonth] = useState<MonthValue>("");
  const [useLocalTime, setUseLocalTime] = useState(false);

  // When modal opens, optionally reset filters
  useEffect(() => {
    if (historyOpen) {
      setFilterYear("");
      setFilterMonth("");
      setUseLocalTime(false);
    }
  }, [historyOpen]);

  // Extract YYYY and MM from a row.start like "2025-10-19 10:00"
  function getYyyyMmFromISO(iso: string): { yyyy: string; mm: MonthValue } {
    const yyyy = iso.slice(0, 4);
    const mm = iso.slice(5, 7) as MonthValue;
    return { yyyy, mm };
  }

  function formatDateTimeWithTimezone(iso: string, useLocal: boolean): string {
    return useLocal
      ? formatDateTimeTZ(iso, { locale: "es-MX", timeZone: "UTC" })
      : formatDateTimeTZ(iso, {
          locale: "es-MX",
          timeZone: "America/Mexico_City",
        });
  }
  // Build years
  function buildYearOptions(rows: Array<{ startISO: string }>): string[] {
    const years = new Set<string>();
    rows.forEach((r) => years.add(getYyyyMmFromISO(r.startISO).yyyy));
    return [
      "All years",
      ...Array.from(years).sort((a, b) => Number(b) - Number(a)),
    ];
  }
  // Update connectionDetailsData when performanceData changes (and rawConnectionData exists)
  useEffect(() => {
    if (rawConnectionData) {
      setCloudDetailsData(mapCloudDetailToSections(rawConnectionData));
    }
  }, [rawConnectionData]);

  // === TABLE SECTION ===
  if (data.table) {
    const isHistorical = data.header === "Historical";
    const allRows = (data.table.data ?? []) as Array<{
      id: number;
      status: string;
      start: string;
      end: string;
      duration: string;
      startISO: string;
      endISO: string;
      statusLabel: string;
      statusVariant: ServiceStatusVariant;
    }>;
    const fullHistoryRows = (data.meta?.fullHistory ?? []) as Array<{
      id: number;
      status: string;
      start: string;
      end: string;
      duration: string;
      startISO: string;
      endISO: string;
      statusLabel: string;
      statusVariant: ServiceStatusVariant;
    }>;
    const yearOptions = useMemo(
      () => buildYearOptions(fullHistoryRows),
      [fullHistoryRows]
    );

    // Apply filters (month+year) to rows for the modal
    const filteredRows = useMemo(() => {
      return fullHistoryRows.filter((r) => {
        const { yyyy, mm } = getYyyyMmFromISO(r.startISO);
        const yearPass =
          !filterYear || filterYear === "All years" || yyyy === filterYear;
        const monthPass = !filterMonth || mm === filterMonth;
        return yearPass && monthPass;
      });
    }, [fullHistoryRows, filterYear, filterMonth]);

    const rowsForTable = fullHistoryRows.slice(0, 3);

    //Columns
    const mainTableColumns: GenericTableColumn<HistoryRow>[] = useMemo(
      () => [
        {
          header: "Status",
          sortAccessor: (row) => row.statusLabel,
          renderCell: (row) => (
            <GenericTag
              label={row.statusLabel}
              variant={row.statusVariant}
              size="small"
              minWidth="50px"
              className="flex justify-center items-center"
            />
          ),
        },
        {
          header: "Start",
          accessor: "start",
          renderCell: (row) => formatDateTimeWithTimezone(row.startISO, false),
        },
        {
          header: "End",
          accessor: "end",
          renderCell: (row) => formatDateTimeWithTimezone(row.endISO, false),
        },
        { header: "Duration", accessor: "duration" },
      ],
      []
    );

    const modalColumns: GenericTableColumn<HistoryRow>[] = useMemo(
      () => [
        {
          header: "Status",
          sortAccessor: (row) => row.statusLabel,
          renderCell: (row) => (
            <GenericTag
              label={row.statusLabel}
              variant={row.statusVariant}
              size="small"
              minWidth="50px"
              className="flex justify-center items-center"
            />
          ),
        },
        {
          header: "Start",
          accessor: "start",
          renderCell: (row) =>
            formatDateTimeWithTimezone(row.startISO, useLocalTime),
        },
        {
          header: "End",
          accessor: "end",
          renderCell: (row) =>
            formatDateTimeWithTimezone(row.endISO, useLocalTime),
        },
        { header: "Duration", accessor: "duration" },
      ],
      [useLocalTime]
    );

    return (
      <>
        <GenericCard
          {...tableFold}
          bodyContent={
            <div className="space-y-2">
              {/* Optional subtitles */}
              {data.items
                .filter((item) => item.type === "subtitle")
                .map((item, i) => (
                  <h4
                    key={i}
                    className="text-sm font-medium text-semantic-text-secondary"
                  >
                    {item.label}
                  </h4>
                ))}

              {/* Toolbar with button (only for Historical) */}
              {isHistorical && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-semantic-text-secondary">
                    Showing recent entries
                  </p>
                  <GenericButton
                    size="sm"
                    variant="secondary"
                    label="View full history"
                    onClick={() => setHistoryOpen(true)}
                    disabled={allRows.length === 0}
                  ></GenericButton>
                </div>
              )}

              {/* Table */}
              <GenericTable
                mobileCards
                columns={
                  isHistorical
                    ? mainTableColumns
                    : (data.table?.columns as any[]) ?? []
                }
                data={
                  isHistorical
                    ? rowsForTable
                    : (data.table?.data as any[]) ?? []
                }
                tableClassName="w-full"
                initialRowsPerPage={3}
                rowsPerPageOptions={[5, 10]}
                isFooterVisible={false}
              />
            </div>
          }
        />

        {/* History Modal */}
        {isHistorical && (
          <GenericModal
            isOpen={historyOpen}
            onClose={() => setHistoryOpen(false)}
            title="Historical details"
          >
            <div className="space-y-3" style={{ minHeight: "500px" }}>
              {/* Filter bar */}
              <div className="flex flex-wrap items-end gap-3">
                {/* Year */}
                <div className="flex flex-col">
                  <label className="text-xs text-semantic-text-secondary mb-1">
                    Year
                  </label>
                  <GenericDropdown
                    items={yearOptions}
                    value={filterYear || "All years"}
                    onSelect={(val) =>
                      setFilterYear(val === "All years" ? "" : val)
                    }
                    size="sm"
                    widthClass="w-32"
                    defaultLabel="All years"
                  />
                </div>

                {/* Month */}
                <div className="flex flex-col">
                  <label className="text-xs text-semantic-text-secondary mb-1">
                    Month
                  </label>
                  <GenericDropdown
                    items={["All months", ...MONTHS.map((m) => m.label)]}
                    value={
                      MONTHS.find((m) => m.value === filterMonth)?.label ??
                      "All months"
                    }
                    onSelect={(label) => {
                      if (label === "All months") {
                        setFilterMonth("");
                        return;
                      }
                      const found = MONTHS.find((m) => m.label === label);
                      setFilterMonth((found?.value ?? "") as MonthValue);
                    }}
                    size="sm"
                    widthClass="w-32"
                    defaultLabel="All months"
                  />
                </div>
                {/* Timezone Toggle */}
                <div className="flex flex-col">
                  <label className="text-xs text-semantic-text-secondary mb-1">
                    Timezone
                  </label>
                  <GenericButton
                    size="sm"
                    variant={useLocalTime ? "brand" : "secondary"}
                    label={useLocalTime ? "Local Time" : "GMT-6"}
                    onClick={() => setUseLocalTime(!useLocalTime)}
                  />
                </div>

                <div className="flex-1" />

                <GenericButton
                  size="sm"
                  variant="secondary"
                  label="Clear"
                  onClick={() => {
                    setFilterYear("");
                    setFilterMonth("");
                  }}
                ></GenericButton>
              </div>

              {/* Filtered table */}
              <div
                style={{
                  minHeight: "350px",
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                <GenericTable
                  columns={modalColumns}
                  data={filteredRows}
                  tableClassName="w-full max-h-96 overflow-y-auto"
                  initialRowsPerPage={5}
                  rowsPerPageOptions={[5, 10]}
                  isFooterVisible={true}
                />
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xs text-semantic-text-secondary">
                  {filteredRows.length}{" "}
                  {filteredRows.length === 1 ? "entry" : "entries"}
                </span>
                <GenericButton
                  size="sm"
                  variant="brand"
                  label="Close"
                  onClick={() => setHistoryOpen(false)}
                ></GenericButton>
              </div>
            </div>
          </GenericModal>
        )}
      </>
    );
  }

  return (
    <>
      <GenericCard
        {...fold}
        bodyContent={
          <div className="space-y-2">
            {data.items.map((item, idx) => {
              if (item.type === "subtitle") {
                return (
                  <div key={idx} className="pt-3 pb-1">
                    <h4 className="text-sm font-medium text-semantic-text-secondary">
                      {item.label}
                    </h4>
                  </div>
                );
              }
              if (item.label === "Service status") {
                return (
                  <div
                    key={idx}
                    className="flex content-center justify-between w-full2 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      {item.label}
                    </h3>
                    {item.tooltip ? (
                      <GenericTooltip
                        text={item.tooltip}
                        position="bottom"
                        horizontalAlign="right"
                        width="20rem"
                      >
                        <span
                          className={`text-sm ${getTextColor(
                            item.label,
                            String(item.value || "")
                          )}`}
                        >
                          {item.value || "-"}
                        </span>
                      </GenericTooltip>
                    ) : (
                      <p
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value || "")
                        )}`}
                      >
                        {item.value || "-"}
                      </p>
                    )}
                  </div>
                );
              }
              if (item.label === "Connection Name") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      Connection Name
                    </h3>
                    <div className="flex items-center gap-2">
                      <GenericTag
                        label={meta.nameValue || "—"}
                        variant="neutral"
                        size="small"
                        minWidth="100px"
                        className="text-center"
                        editable={true}
                        value={meta.nameValue}
                        onSave={(newValue) => {
                          meta.saveName(newValue);
                          meta.setNameValue(newValue);
                        }}
                        onCancel={() => {
                          meta.cancelName();
                        }}
                        isSaving={meta.savingField === "name"}
                        disabled={meta.savingField !== null}
                      />
                    </div>
                  </div>
                );
              }
              if (item.label === "Tag") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                      <EditableTagList
                        tags={tagArray}
                        onUpdate={meta.updateTagArray}
                        disabled={meta.savingField !== null}
                        isLoading={meta.isSavingTag}
                        resourceType="ke-cloud-connectivity"
                        resourceId={connectionId}
                        initialAssignedTags={initialAssignedTags}
                        tenantId={resourceTenantId}
                      />
                    </div>
                  </div>
                );
              }

              // Parent Connection - make it clickeable to navigate to port details
              if (item.label === "Parent Connection") {
                const parentConnectionValue = String(item.value || "-");
                // Extract subid from parentConnectionValue (e.g., "[PORT]10-7E2C9A" or "[LAG]...")
                const subidMatch =
                  parentConnectionValue.match(/\[(PORT|LAG)\](.*)/);
                const subid = subidMatch ? subidMatch[2] : null;
                // Use parentConnectionId which handles both PORT and LAG cases
                // Fallback to port.id if parentConnectionId is not available
                const parentConnectionId =
                  connectionDetail?.parentConnectionId ||
                  (connectionDetail?.port?.id
                    ? String(connectionDetail.port.id)
                    : null);

                // Only make it clickeable if we have a parentConnectionId
                if (
                  parentConnectionId &&
                  parentConnectionId !== "-" &&
                  parentConnectionValue !== "-"
                ) {
                  return (
                    <div
                      key={idx}
                      className={`flex justify-between w-full py-1 ${
                        idx !== data.items.length - 1 ? "border-b" : ""
                      }`}
                    >
                      <h3 className="text-sm text-semantic-text-primary">
                        {item.label}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          {...viewHref(buildDetailPath("port", { portId: parentConnectionId }, { subid }))}
                          onClick={() => {
                            navigate(
                              buildDetailPath("port", { portId: parentConnectionId }, { subid }),
                              {
                                state: {
                                  portType: connectionDetail?.port?.portType as
                                    | "physical"
                                    | "virtual"
                                    | undefined,
                                },
                              }
                            );
                          }}
                          className="
                            hover:text-semantic-text-brand 
                            hover:underline
                            focus:underline 
                            active:underline
                            font-medium
                            underline
                            text-semantic-text-brand
                            text-sm
                          "
                        >
                          {parentConnectionValue}
                        </button>
                      </div>
                    </div>
                  );
                }
                // If no portId, render as normal text
                return (
                  <div
                    key={idx}
                    className={`flex justify-between w-full py-1 ${
                      idx !== data.items.length - 1 ? "border-b" : ""
                    }`}
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      {item.label}
                    </h3>
                    <div className="flex items-center gap-2">
                      <div
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value || "")
                        )}`}
                      >
                        {item.value || "-"}
                      </div>
                    </div>
                  </div>
                );
              }

              // Default: render a non-editable key-value row
              return (
                <div
                  key={idx}
                  className={`${item.statusVariant ? "hidden sm:flex" : "flex"} justify-between w-full py-1 ${
                    idx !== data.items.length - 1 ? "border-b" : ""
                  }`}
                >
                  <h3 className="text-sm text-semantic-text-primary">
                    {item.label}
                  </h3>
                  <div className="flex items-center gap-2">
                    {item.Icon && (
                      <item.Icon className="w-6 h-6 text-gray-500" />
                    )}
                    {item.statusVariant ? (
                      <ServiceStatusValue
                        label={String(item.value ?? "-")}
                        variant={item.statusVariant}
                        tooltip={item.tooltip}
                      />
                    ) : /* Leaves the app, so it is an anchor rather than
                          anything clickable we build: opening in a new tab and
                          copying the address are the point of a row that sends
                          someone to their cloud provider, and those come free.
                          `noopener` because `_blank` would otherwise hand the
                          opened page a handle on this one. Styled like the only
                          other external link in the app, in `EmptyItems.tsx`. */
                    item.externalLink ? (
                      <a
                        href={item.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-semantic-text-brand underline hover:opacity-80"
                      >
                        {item.value || "-"}
                      </a>
                    ) : item.type === "tag" ? (
                      <GenericTag
                        label={String(item.value)}
                        variant="success"
                      />
                    ) : (
                      <div
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value || "")
                        )} ${
                          item.label === "Cloud Connection ID"
                            ? "max-w-xs overflow-x-auto whitespace-nowrap custom-scrollbar cursor-pointer"
                            : ""
                        }`}
                        title={
                          item.label === "Cloud Connection ID"
                            ? String(item.value || "")
                            : undefined
                        }
                      >
                        {item.value || "-"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        }
      />
      <GenericAlert
        isOpen={alertOpen}
        onClose={closeAlert}
        onConfirm={confirmAlert}
        title="Error Updating Connection"
        description={alertMessage}
        variant="error"
        confirmLabel="OK"
      />
    </>
  );
};

export default CloudDetailsCard;
